/**
 * DesktopIcons.jsx — Windows-style desktop shortcuts & Android-style App Folders.
 *
 * Supports:
 *   - Clicking a shortcut icon to open / restore / focus its linked widget window
 *   - Clicking a folder icon to pop up a floating Android-style Folder Bubble
 *     (clicking anywhere off the bubble dismisses it immediately)
 *   - Dragging icons across the desktop with grid-snapping
 *   - Dragging one shortcut onto another shortcut or folder to create/populate an Android Folder
 *   - Dragging apps out of an open folder bubble onto the desktop to un-folder them
 *   - Dragging widgets out of the HomeMenu (Start Menu) onto the desktop or into a folder
 *   - Renaming shortcuts and folders inline (double-click, ✎, F2, or right-click)
 *   - Automatic two-way settings & geometry persistence with linked widget windows
 */

import { For, Show, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import wm, { summarizeIconSettings, snapIconPosition } from '../lib/wmStore.js';
import { homeMenu } from '../lib/homeMenuStore.js';
import ContextMenu from './ContextMenu.jsx';
import { EMOJI } from '../lib/emoji.js';
import './DesktopIcons.css';

export default function DesktopIcons(props) {
  const [selectedId, setSelectedId] = createSignal(null);
  const [renamingId, setRenamingId] = createSignal(null);
  const [renameDraft, setRenameDraft] = createSignal('');
  const [contextMenu, setContextMenu] = createSignal(null); // { x, y, icon, parentFolderId }
  const [dropTargetId, setDropTargetId] = createSignal(null);
  const [openFolderId, setOpenFolderId] = createSignal(null);
  const [folderBubblePos, setFolderBubblePos] = createSignal({ x: 120, y: 80 });
  const [editingBubbleTitle, setEditingBubbleTitle] = createSignal(false);
  const [bubbleTitleDraft, setBubbleTitleDraft] = createSignal('');
  let bubbleRef = null;

  const icons = createMemo(() => wm.activeDesktopIcons());
  const folders = createMemo(() => icons().filter(ic => ic.kind === 'folder'));
  const openFolder = createMemo(() =>
    icons().find(ic => ic.kind === 'folder' && ic.id === openFolderId()) || null
  );

  const openWidgets = createMemo(() => {
    const dash = wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
    return dash?.widgets || [];
  });

  function isIconWindowOpen(icon) {
    if (!icon) return false;
    if (icon.kind === 'folder') {
      return (icon.items || []).some(sub => isIconWindowOpen(sub));
    }
    return openWidgets().some(
      w => w.id === icon.linkedWidgetId || w._iconId === icon.id || w.settings?._iconId === icon.id
    );
  }

  function computeBubblePos(folderIcon) {
    const desktop = document.querySelector('.v2-desktop');
    const rect = desktop?.getBoundingClientRect?.() || { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight - 48 };
    const bubbleW = 296;
    const bubbleH = 260;
    const fx = rect.left + (Number(folderIcon.x) || 14);
    const fy = rect.top + (Number(folderIcon.y) || 14);
    let x = fx + 92;
    if (x + bubbleW > window.innerWidth - 16) {
      x = Math.max(14, fx - bubbleW - 12);
    }
    let y = fy - 12;
    if (y + bubbleH > window.innerHeight - 58) {
      y = Math.max(14, window.innerHeight - bubbleH - 58);
    }
    return { x: Math.round(x), y: Math.round(y) };
  }

  function toggleFolderBubble(folderIcon) {
    if (!folderIcon || folderIcon.kind !== 'folder') return;
    if (openFolderId() === folderIcon.id) {
      setOpenFolderId(null);
      setEditingBubbleTitle(false);
      return;
    }
    setFolderBubblePos(computeBubblePos(folderIcon));
    setEditingBubbleTitle(false);
    setOpenFolderId(folderIcon.id);
  }

  function closeFolderBubble() {
    if (!openFolderId()) return;
    setOpenFolderId(null);
    setEditingBubbleTitle(false);
  }

  function onGlobalKeyDown(e) {
    if (e.key === 'Escape' && openFolderId() && !editingBubbleTitle() && !renamingId()) {
      closeFolderBubble();
    }
  }

  onMount(() => {
    window.addEventListener('keydown', onGlobalKeyDown);
  });
  onCleanup(() => {
    window.removeEventListener('keydown', onGlobalKeyDown);
  });

  function startRename(icon, e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedId(icon.id);
    setRenameDraft(icon.title || '');
    setRenamingId(icon.id);
  }

  function commitRename(icon) {
    if (renamingId() !== icon.id) return;
    const next = renameDraft().trim();
    setRenamingId(null);
    if (next && next !== icon.title) {
      wm.renameDesktopIcon(icon.id, next);
    }
  }

  function cancelRename() {
    setRenamingId(null);
  }

  function startBubbleTitleEdit(folder, e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setBubbleTitleDraft(folder.title || 'Folder');
    setEditingBubbleTitle(true);
  }

  function commitBubbleTitleEdit(folder) {
    if (!editingBubbleTitle()) return;
    const next = bubbleTitleDraft().trim();
    setEditingBubbleTitle(false);
    if (next && next !== folder.title) {
      wm.renameDesktopIcon(folder.id, next);
    }
  }

  function findDropTargetIcon(curX, curY, selfId) {
    const cx = curX + 44;
    const cy = curY + 44;
    let best = null;
    let bestDist = 46;
    for (const other of icons()) {
      if (!other || other.id === selfId) continue;
      const ox = (Number(other.x) || 14) + 44;
      const oy = (Number(other.y) || 14) + 44;
      const d = Math.hypot(cx - ox, cy - oy);
      if (d < bestDist) {
        bestDist = d;
        best = other;
      }
    }
    return best;
  }

  function handleIconPointerDown(icon, ev) {
    if (ev.button !== 0) return;
    if (ev.target.closest('input, .v2-desktop-icon__rename-btn')) return;
    ev.stopPropagation();
    setSelectedId(icon.id);

    const el = ev.currentTarget;
    const startX = ev.clientX;
    const startY = ev.clientY;
    const origX = Number(icon.x) || 16;
    const origY = Number(icon.y) || 16;
    let moved = false;
    let rafId = 0;
    let curX = origX;
    let curY = origY;

    function onMove(e) {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!moved && Math.hypot(dx, dy) > 5) {
        moved = true;
        el.classList.add('is-dragging');
      }
      if (!moved) return;
      const desktop = document.querySelector('.v2-desktop');
      const rect = desktop?.getBoundingClientRect();
      const maxW = (rect?.width || window.innerWidth) - 88;
      const maxH = (rect?.height || (window.innerHeight - 48)) - 90;
      curX = Math.max(8, Math.min(maxW, origX + dx));
      curY = Math.max(8, Math.min(maxH, origY + dy));

      if (icon.kind !== 'folder') {
        const hoverTarget = findDropTargetIcon(curX, curY, icon.id);
        setDropTargetId(hoverTarget ? hoverTarget.id : null);
      }

      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          el.style.transform = `translate3d(${Math.round(curX)}px, ${Math.round(curY)}px, 0)`;
        });
      }
    }

    function onUp() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      el.classList.remove('is-dragging');
      const targetId = dropTargetId();
      setDropTargetId(null);

      if (moved) {
        if (targetId && icon.kind !== 'folder') {
          const mergedFolderId = wm.mergeIconsIntoFolder(icon.id, targetId);
          if (mergedFolderId) {
            const folderObj = wm.activeDesktopIcons().find(ic => ic.id === mergedFolderId);
            if (folderObj) {
              setFolderBubblePos(computeBubblePos(folderObj));
              setOpenFolderId(mergedFolderId);
            }
            return;
          }
        }
        const snapped = snapIconPosition(curX, curY, icons(), icon.id);
        el.style.transform = `translate3d(${snapped.x}px, ${snapped.y}px, 0)`;
        wm.moveDesktopIcon(icon.id, snapped.x, snapped.y);
      } else if (icon.kind === 'folder') {
        toggleFolderBubble(icon);
      } else {
        closeFolderBubble();
        wm.openDesktopIcon(icon.id);
      }
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  function handleBubbleAppPointerDown(folder, sub, ev) {
    if (ev.button !== 0) return;
    if (ev.target.closest('input, .v2-desktop-icon__rename-btn, .v2-folder-app__unpin')) return;
    ev.stopPropagation();

    const startX = ev.clientX;
    const startY = ev.clientY;
    let moved = false;

    function onMove(e) {
      if (!moved && Math.hypot(e.clientX - startX, e.clientY - startY) > 7) {
        moved = true;
      }
    }

    function onUp(e) {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);

      if (moved) {
        const bRect = bubbleRef?.getBoundingClientRect?.();
        const outsideBubble = !bRect ||
          e.clientX < bRect.left || e.clientX > bRect.right ||
          e.clientY < bRect.top || e.clientY > bRect.bottom;
        if (outsideBubble) {
          const desktop = document.querySelector('.v2-desktop');
          const dRect = desktop?.getBoundingClientRect?.() || { left: 0, top: 0 };
          wm.extractShortcutFromFolder(folder.id, sub.id, {
            x: e.clientX - dRect.left - 44,
            y: e.clientY - dRect.top - 44,
          });
        }
      } else {
        closeFolderBubble();
        wm.openDesktopIcon(sub.id);
      }
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  function handleIconKeyDown(icon, e) {
    if (renamingId() === icon.id) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (icon.kind === 'folder') {
        toggleFolderBubble(icon);
      } else {
        wm.openDesktopIcon(icon.id);
      }
    } else if (e.key === 'F2') {
      e.preventDefault();
      startRename(icon, e);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      wm.removeDesktopIcon(icon.id);
    } else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      setContextMenu({ x: rect.left + 28, y: rect.bottom + 4, icon, parentFolderId: null });
    }
  }

  function openIconContextMenu(icon, e, parentFolderId = null) {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(icon.id);
    setContextMenu({ x: e.clientX, y: e.clientY, icon, parentFolderId });
  }

  function contextMenuItems() {
    const ctx = contextMenu();
    const icon = ctx?.icon;
    if (!icon) return [];

    if (icon.kind === 'folder') {
      const isBubbleOpen = openFolderId() === icon.id;
      const hasItems = (icon.items || []).length > 0;
      return [
        {
          icon: isBubbleOpen ? '×' : '📂',
          label: isBubbleOpen ? 'Close folder bubble' : 'Open folder',
          iconTone: 'theme',
          onSelect: () => toggleFolderBubble(icon),
        },
        {
          icon: '✎',
          label: 'Rename folder',
          onSelect: () => startRename(icon),
        },
        {
          icon: '🚀',
          label: 'Launch all in folder',
          disabled: !hasItems,
          onSelect: () => {
            for (const sub of icon.items || []) {
              wm.openDesktopIcon(sub.id);
            }
          },
        },
        {
          icon: '📤',
          label: 'Ungroup folder to desktop',
          disabled: !hasItems,
          onSelect: () => {
            if (openFolderId() === icon.id) closeFolderBubble();
            wm.ungroupDesktopFolder(icon.id);
          },
        },
        { separator: true },
        {
          icon: '×',
          label: 'Delete folder',
          danger: true,
          onSelect: () => {
            if (openFolderId() === icon.id) closeFolderBubble();
            wm.removeDesktopIcon(icon.id);
          },
        },
      ];
    }

    const isOpen = isIconWindowOpen(icon);
    const parentFolderId = ctx?.parentFolderId;
    const availableFolders = folders();

    const folderMoveItems = parentFolderId
      ? [
          {
            icon: '📤',
            label: 'Move out to desktop',
            iconTone: 'theme',
            onSelect: () => wm.extractShortcutFromFolder(parentFolderId, icon.id),
          },
        ]
      : [
          ...availableFolders.slice(0, 4).map(f => ({
            icon: '📁',
            label: `Move to “${f.title}”`,
            onSelect: () => wm.mergeIconsIntoFolder(icon.id, f.id),
          })),
          {
            icon: '➕',
            label: 'Put in new folder',
            onSelect: () => {
              const folder = wm.createDesktopFolder({
                title: `${icon.title} Folder`.slice(0, 28),
                x: icon.x,
                y: icon.y,
                silent: true,
              });
              if (folder) {
                wm.mergeIconsIntoFolder(icon.id, folder.id);
                const updated = wm.activeDesktopIcons().find(ic => ic.id === folder.id);
                if (updated) {
                  setFolderBubblePos(computeBubblePos(updated));
                  setOpenFolderId(folder.id);
                }
              }
            },
          },
        ];

    return [
      {
        icon: isOpen ? '◉' : '↗',
        label: isOpen ? 'Focus linked window' : 'Open shortcut',
        iconTone: 'theme',
        onSelect: () => {
          closeFolderBubble();
          wm.openDesktopIcon(icon.id);
        },
      },
      {
        icon: '➕',
        label: 'Open as new window',
        onSelect: () => {
          closeFolderBubble();
          wm.openDesktopIcon(icon.id, { forceNew: true });
        },
      },
      {
        icon: '✎',
        label: 'Rename shortcut',
        onSelect: () => startRename(icon),
      },
      {
        icon: EMOJI.settings,
        label: 'Open widget settings',
        onSelect: () => {
          closeFolderBubble();
          const wid = wm.openDesktopIcon(icon.id);
          if (wid) {
            requestAnimationFrame(() => {
              window.dispatchEvent(new CustomEvent('v2-open-window-settings', { detail: { widgetId: wid } }));
            });
          }
        },
      },
      { separator: true },
      ...folderMoveItems,
      { separator: true },
      {
        icon: '×',
        label: parentFolderId ? 'Remove from folder' : 'Remove from desktop',
        danger: true,
        onSelect: () => wm.removeDesktopIcon(icon.id),
      },
    ];
  }

  return (
    <div
      class="v2-desktop-icons"
      aria-label="Desktop shortcuts"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) {
          setSelectedId(null);
          closeFolderBubble();
        }
      }}
    >
      <Show when={props.dropGhost}>
        <div
          class="v2-desktop-icons__drop-ghost"
          style={{
            transform: `translate3d(${props.dropGhost.x}px, ${props.dropGhost.y}px, 0)`,
          }}
        />
      </Show>

      <For each={icons()}>
        {(icon) => {
          const isFolder = () => icon.kind === 'folder';
          const meta = createMemo(() =>
            isFolder() ? null : wm.state.catalog.find(c => c.type === icon.widget_type)
          );
          const subText = createMemo(() => {
            if (isFolder()) {
              const count = (icon.items || []).length;
              return `${count} app${count === 1 ? '' : 's'}`;
            }
            return summarizeIconSettings(icon.widget_type, icon.settings);
          });
          const isOpen = createMemo(() => isIconWindowOpen(icon));
          let inputRef;

          return (
            <div
              class="v2-desktop-icon"
              data-icon-id={icon.id}
              data-icon-kind={isFolder() ? 'folder' : 'shortcut'}
              classList={{
                'v2-desktop-icon--folder': isFolder(),
                'is-selected': selectedId() === icon.id,
                'is-open': isOpen(),
                'is-folder-open': isFolder() && openFolderId() === icon.id,
                'is-drop-target': dropTargetId() === icon.id,
              }}
              style={{
                transform: `translate3d(${icon.x || 16}px, ${icon.y || 16}px, 0)`,
              }}
              tabIndex="0"
              role="button"
              aria-label={
                isFolder()
                  ? `${icon.title || 'Folder'} (${(icon.items || []).length} apps)`
                  : `${(icon.title && icon.title !== 'undefined') ? icon.title : (meta()?.title || icon.widget_type || 'Shortcut')} desktop shortcut`
              }
              data-hint={
                isFolder()
                  ? `${icon.title || 'Folder'} (${subText()})\nClick to open Android folder bubble · Drag shortcuts here to group`
                  : `${icon.title || meta()?.title || icon.widget_type}${subText() ? ` (${subText()})` : ''}\nClick to open · Drag onto another icon to create a folder · Right-click or ✎ to rename`
              }
              onPointerDown={(e) => handleIconPointerDown(icon, e)}
              onKeyDown={(e) => handleIconKeyDown(icon, e)}
              onContextMenu={(e) => openIconContextMenu(icon, e)}
            >
              <Show
                when={isFolder()}
                fallback={
                  <div class="v2-desktop-icon__tile">
                    <span class="v2-desktop-icon__emoji">{icon.emoji || meta()?.emoji || EMOJI.window}</span>
                    <span class="v2-desktop-icon__shortcut-badge" aria-hidden="true">↗</span>
                    <Show when={isOpen()}>
                      <span class="v2-desktop-icon__active-dot" data-hint="Window open on desktop" />
                    </Show>
                  </div>
                }
              >
                <div class="v2-desktop-icon__tile v2-desktop-icon__tile--folder">
                  <div class="v2-desktop-icon__folder-grid">
                    <Show
                      when={(icon.items || []).length > 0}
                      fallback={<span class="v2-desktop-icon__folder-empty">📁</span>}
                    >
                      <For each={(icon.items || []).slice(0, 4)}>
                        {(sub) => {
                          const subMeta = wm.state.catalog.find(c => c.type === sub.widget_type);
                          return (
                            <span class="v2-desktop-icon__folder-mini">
                              {sub.emoji || subMeta?.emoji || EMOJI.window}
                            </span>
                          );
                        }}
                      </For>
                    </Show>
                  </div>
                  <span class="v2-desktop-icon__folder-count" aria-hidden="true">
                    {(icon.items || []).length}
                  </span>
                  <Show when={isOpen()}>
                    <span class="v2-desktop-icon__active-dot" data-hint="App inside folder is open" />
                  </Show>
                </div>
              </Show>

              <div class="v2-desktop-icon__label-wrap">
                <Show
                  when={renamingId() === icon.id}
                  fallback={
                    <>
                      <button
                        type="button"
                        class="v2-desktop-icon__rename-btn"
                        data-hint={isFolder() ? 'Rename folder (F2)' : 'Rename shortcut (F2)'}
                        aria-label={`Rename ${icon.title}`}
                        onClick={(e) => startRename(icon, e)}
                      >
                        ✎
                      </button>
                      <span
                        class="v2-desktop-icon__label"
                        onDblClick={(e) => startRename(icon, e)}
                      >
                        {icon.title || meta()?.title || icon.widget_type}
                      </span>
                      <Show when={subText()}>
                        <span class="v2-desktop-icon__sub">{subText()}</span>
                      </Show>
                    </>
                  }
                >
                  <input
                    ref={(el) => {
                      inputRef = el;
                      queueMicrotask(() => {
                        inputRef?.focus();
                        inputRef?.select();
                      });
                    }}
                    type="text"
                    class="v2-desktop-icon__input"
                    value={renameDraft()}
                    maxLength={40}
                    aria-label={isFolder() ? 'Folder name' : 'Shortcut name'}
                    onInput={(e) => setRenameDraft(e.currentTarget.value)}
                    onBlur={() => commitRename(icon)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitRename(icon);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRename();
                      }
                    }}
                  />
                </Show>
              </div>
            </div>
          );
        }}
      </For>

      {/* ── Android-style Floating Folder Bubble Popover ─────────── */}
      <Show when={openFolder()}>
        {(folder) => {
          let titleInputRef;
          return (
            <Portal>
              <div
                class="v2-folder-backdrop"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  closeFolderBubble();
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  closeFolderBubble();
                }}
              />
              <div
                ref={(el) => (bubbleRef = el)}
                class="v2-folder-bubble"
                role="dialog"
                aria-label={`${folder().title} folder`}
                data-folder-bubble-id={folder().id}
                style={{
                  left: `${folderBubblePos().x}px`,
                  top: `${folderBubblePos().y}px`,
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div class="v2-folder-bubble__hdr">
                  <span class="v2-folder-bubble__icon" aria-hidden="true">📂</span>
                  <Show
                    when={editingBubbleTitle()}
                    fallback={
                      <button
                        type="button"
                        class="v2-folder-bubble__title-btn"
                        data-hint="Click to rename folder"
                        aria-label={`Rename folder ${folder().title}`}
                        onClick={(e) => startBubbleTitleEdit(folder(), e)}
                      >
                        <span class="v2-folder-bubble__title-text">{folder().title}</span>
                        <span class="v2-folder-bubble__title-edit" aria-hidden="true">✎</span>
                      </button>
                    }
                  >
                    <input
                      ref={(el) => {
                        titleInputRef = el;
                        queueMicrotask(() => {
                          titleInputRef?.focus();
                          titleInputRef?.select();
                        });
                      }}
                      type="text"
                      class="v2-folder-bubble__title-input"
                      value={bubbleTitleDraft()}
                      maxLength={36}
                      aria-label="Folder name"
                      onInput={(e) => setBubbleTitleDraft(e.currentTarget.value)}
                      onBlur={() => commitBubbleTitleEdit(folder())}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitBubbleTitleEdit(folder());
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingBubbleTitle(false);
                        }
                      }}
                    />
                  </Show>
                  <span class="v2-folder-bubble__count">
                    {(folder().items || []).length} app{(folder().items || []).length === 1 ? '' : 's'}
                  </span>
                  <Show when={(folder().items || []).length > 0}>
                    <button
                      type="button"
                      class="v2-folder-bubble__act"
                      data-hint="Ungroup all apps onto desktop"
                      aria-label="Ungroup folder to desktop"
                      onClick={() => {
                        const fid = folder().id;
                        closeFolderBubble();
                        wm.ungroupDesktopFolder(fid);
                      }}
                    >
                      📤
                    </button>
                  </Show>
                  <button
                    type="button"
                    class="v2-folder-bubble__act"
                    data-hint="Close folder (Esc)"
                    aria-label="Close folder"
                    onClick={closeFolderBubble}
                  >
                    ×
                  </button>
                </div>

                <div class="v2-folder-bubble__grid">
                  <Show
                    when={(folder().items || []).length > 0}
                    fallback={
                      <div class="v2-folder-bubble__empty">
                        <span>Folder is empty. Drag shortcuts onto this folder or add widgets from the Start Menu.</span>
                        <button
                          type="button"
                          class="v2-btn-sm"
                          onClick={() => {
                            closeFolderBubble();
                            homeMenu.openWith('widgets');
                          }}
                        >
                          + Browse Widgets
                        </button>
                      </div>
                    }
                  >
                    <For each={folder().items || []}>
                      {(sub) => {
                        const subMeta = createMemo(() => wm.state.catalog.find(c => c.type === sub.widget_type));
                        const subBadge = createMemo(() => summarizeIconSettings(sub.widget_type, sub.settings));
                        const subOpen = createMemo(() => isIconWindowOpen(sub));
                        let subInputRef;

                        return (
                          <div
                            class="v2-folder-app"
                            data-folder-item-id={sub.id}
                            tabIndex="0"
                            role="button"
                            aria-label={`${(sub.title && sub.title !== 'undefined') ? sub.title : (subMeta()?.title || sub.widget_type || 'Shortcut')} shortcut`}
                            data-hint={`${sub.title || subMeta()?.title || sub.widget_type}${subBadge() ? ` (${subBadge()})` : ''}\nClick to launch · Drag outside bubble to move to desktop`}
                            onPointerDown={(e) => handleBubbleAppPointerDown(folder(), sub, e)}
                            onKeyDown={(e) => {
                              if (renamingId() === sub.id) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                closeFolderBubble();
                                wm.openDesktopIcon(sub.id);
                              } else if (e.key === 'F2') {
                                e.preventDefault();
                                startRename(sub, e);
                              }
                            }}
                            onContextMenu={(e) => openIconContextMenu(sub, e, folder().id)}
                          >
                            <button
                              type="button"
                              class="v2-folder-app__unpin"
                              data-hint="Move shortcut out to desktop"
                              aria-label={`Move ${sub.title} to desktop`}
                              onClick={(e) => {
                                e.stopPropagation();
                                wm.extractShortcutFromFolder(folder().id, sub.id);
                              }}
                            >
                              📤
                            </button>
                            <div class="v2-desktop-icon__tile">
                              <span class="v2-desktop-icon__emoji">
                                {sub.emoji || subMeta()?.emoji || EMOJI.window}
                              </span>
                              <span class="v2-desktop-icon__shortcut-badge" aria-hidden="true">↗</span>
                              <Show when={subOpen()}>
                                <span class="v2-desktop-icon__active-dot" data-hint="Window open" />
                              </Show>
                            </div>
                            <div class="v2-desktop-icon__label-wrap">
                              <Show
                                when={renamingId() === sub.id}
                                fallback={
                                  <>
                                    <span
                                      class="v2-desktop-icon__label"
                                      onDblClick={(e) => startRename(sub, e)}
                                    >
                                      {sub.title || subMeta()?.title || sub.widget_type}
                                    </span>
                                    <Show when={subBadge()}>
                                      <span class="v2-desktop-icon__sub">{subBadge()}</span>
                                    </Show>
                                  </>
                                }
                              >
                                <input
                                  ref={(el) => {
                                    subInputRef = el;
                                    queueMicrotask(() => {
                                      subInputRef?.focus();
                                      subInputRef?.select();
                                    });
                                  }}
                                  type="text"
                                  class="v2-desktop-icon__input"
                                  value={renameDraft()}
                                  maxLength={40}
                                  aria-label="Shortcut name"
                                  onInput={(e) => setRenameDraft(e.currentTarget.value)}
                                  onBlur={() => commitRename(sub)}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      commitRename(sub);
                                    } else if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelRename();
                                    }
                                  }}
                                />
                              </Show>
                            </div>
                          </div>
                        );
                      }}
                    </For>
                  </Show>
                </div>

                <div class="v2-folder-bubble__footer">
                  <span>Click app to open · Drag out to desktop</span>
                  <span>Click outside to close</span>
                </div>
              </div>
            </Portal>
          );
        }}
      </Show>

      <ContextMenu
        open={!!contextMenu()}
        x={contextMenu()?.x}
        y={contextMenu()?.y}
        label="Shortcut actions"
        items={contextMenuItems()}
        onClose={() => setContextMenu(null)}
      />
    </div>
  );
}

